import { useState, useCallback, useEffect, useRef } from "react";
import { Star } from "lucide-react";
import {
    APP_STORE,
    REVIEWS_ROW_1,
    REVIEWS_ROW_2,
    REVIEWS_ALL,
} from "../data/reviews";

// Re-export so existing imports from this file keep working.
export { REVIEWS_ROW_1, REVIEWS_ROW_2, REVIEWS_ALL };

export function PlatformIcon({ platform }) {
    if (platform === APP_STORE) {
        return (
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-white/55">
                <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current">
                    <path d="M16.365 1.43c0 1.14-.44 2.22-1.17 3-.78.85-2.04 1.5-3.1 1.41-.12-1.06.4-2.16 1.12-2.87.79-.8 2.13-1.41 3.15-1.54zM20.5 17.04c-.51 1.17-.76 1.69-1.41 2.72-.91 1.44-2.2 3.24-3.8 3.25-1.42.02-1.79-.93-3.72-.92-1.93.01-2.34.93-3.76.92-1.6-.01-2.82-1.63-3.74-3.07C1.56 16.8 1.3 11.87 2.88 9.27c1.13-1.87 2.91-2.96 4.58-2.96 1.71 0 2.79.94 4.2.94 1.38 0 2.22-.94 4.2-.94 1.49 0 3.07.81 4.2 2.22-3.69 2.02-3.09 7.29.44 8.51z" />
                </svg>
                App Store
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-white/55">
            <svg viewBox="0 0 24 24" className="w-3 h-3">
                <path fill="#34a853" d="M3.6 1.8a1.5 1.5 0 0 0-.5 1.1v18.2c0 .4.2.8.5 1.1L13.8 12z" />
                <path fill="#fbbc04" d="m16.8 15-3-3 3-3 3.6 2c1.2.7 1.2 2.5 0 3.2z" />
                <path fill="#ea4335" d="M13.8 12 3.6 1.8a1.5 1.5 0 0 1 1.7-.2L16.8 9z" />
                <path fill="#4285f4" d="M13.8 12 16.8 15 5.3 22.4a1.5 1.5 0 0 1-1.7-.2z" />
            </svg>
            Google Play
        </span>
    );
}

function Stars({ rating = 5 }) {
    return (
        <div
            className="inline-flex items-center gap-0.5"
            aria-label={`${rating} out of 5 stars`}
        >
            {[1, 2, 3, 4, 5].map((i) => {
                const filled = i <= rating;
                return (
                    <Star
                        key={i}
                        size={13}
                        strokeWidth={filled ? 0 : 1.5}
                        fill={filled ? "currentColor" : "none"}
                        className={
                            filled
                                ? "text-[#49bef5] drop-shadow-[0_0_4px_rgba(73,190,245,0.5)]"
                                : "text-white/25"
                        }
                    />
                );
            })}
        </div>
    );
}

export function ReviewCard({ r, highlighted = false, cardIdx }) {
    return (
        <article
            data-testid={`review-card-${r.author.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            data-card-idx={cardIdx}
            data-highlighted={highlighted || undefined}
            className={`shrink-0 w-[320px] sm:w-[360px] tile p-5 mr-4 select-none transition-all duration-200 ${
                highlighted
                    ? "!border-[#49bef5]/60 -translate-y-0.5 shadow-[0_0_24px_-4px_rgba(73,190,245,0.35)]"
                    : ""
            }`}
        >
            <div className="flex items-center justify-between mb-3">
                <Stars rating={r.rating || 5} />
                <PlatformIcon platform={r.platform} />
            </div>
            <h3 className="font-display font-semibold text-white text-[16px] leading-snug mb-2">{r.title}</h3>
            <p className="text-white/65 text-[14px] leading-relaxed mb-4 line-clamp-4">{r.body}</p>
            <div className="flex items-center justify-between text-[11px] font-mono text-white/40 uppercase tracking-widest">
                <span>{r.author}</span>
                <span>{r.date}</span>
            </div>
        </article>
    );
}

/**
 * Shared marquee row.
 *
 * Props:
 *   items       — array of review objects
 *   reverse     — reverse scroll direction (default false)
 *   speed       — animation duration in seconds (default 60)
 *   pauseOnTap  — enable tap-toggle pause (default false, used by embed)
 *   interactive — switch from CSS animation to a natively-scrollable
 *                 container with rAF-driven auto-advance. Enables
 *                 finger-swipe scrubbing forward/back while auto-scroll
 *                 continues from wherever the user releases. Also
 *                 highlights the tapped card. Used only by the embed.
 *
 * Note: `py-1` on the overflow container prevents the `.tile:hover`
 * `translateY(-2px)` from clipping the top edge of hovered cards.
 */
export default function ReviewsMarquee({
    items,
    reverse = false,
    speed = 60,
    pauseOnTap = false,
    interactive = false,
}) {
    const [paused, setPaused] = useState(false);
    const [highlightedIdx, setHighlightedIdx] = useState(-1);
    const scrollRef = useRef(null);
    const rafRef = useRef(null);
    const touchStartRef = useRef(null);
    const isDraggingRef = useRef(false);

    const doubled = [...items, ...items];

    const togglePause = useCallback(() => {
        if (pauseOnTap) setPaused((p) => !p);
    }, [pauseOnTap]);

    // Interactive path: natively-scrollable container driven by rAF.
    // Auto-advance ticks scrollLeft += pxPerFrame each frame at landing
    // parity (~0.75px/frame for a 60s cycle of doubled 17-card content).
    // Native touch takes over on drag; we resume from wherever the user
    // released. Wrap silently at scrollWidth/2 since content is doubled.
    useEffect(() => {
        if (!interactive) return;
        const el = scrollRef.current;
        if (!el) return;

        // Landing marquee moves scrollWidth/2 across `speed` seconds.
        // pxPerFrame @ 60fps = (scrollWidth / 2) / (speed * 60).
        // Recompute on layout via getBoundingClientRect fallback.
        const getPxPerFrame = () => {
            const half = el.scrollWidth / 2;
            return half / (speed * 60);
        };

        const tick = () => {
            if (!paused && !isDraggingRef.current && el) {
                const pxPerFrame = getPxPerFrame();
                let next = el.scrollLeft + (reverse ? -pxPerFrame : pxPerFrame);
                const half = el.scrollWidth / 2;
                if (next >= half) next -= half;
                else if (next < 0) next += half;
                el.scrollLeft = next;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [interactive, paused, reverse, speed]);

    // Touch handlers for interactive path: distinguish tap from swipe.
    // Tap  (delta < 8px, duration < 300ms) → toggle pause + highlight card.
    // Swipe (delta ≥ 8px)                  → native scroll takes over.
    const onTouchStart = (e) => {
        if (!interactive) return;
        const t = e.touches[0];
        touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
        isDraggingRef.current = false;
    };
    const onTouchMove = (e) => {
        if (!interactive || !touchStartRef.current) return;
        const t = e.touches[0];
        const dx = Math.abs(t.clientX - touchStartRef.current.x);
        if (dx >= 8) isDraggingRef.current = true;
    };
    const onTouchEnd = (e) => {
        if (!interactive || !touchStartRef.current) return;
        const dt = Date.now() - touchStartRef.current.t;
        const wasDrag = isDraggingRef.current;
        touchStartRef.current = null;

        if (!wasDrag && dt < 300) {
            // Tap → identify card under the touch, highlight it, pause.
            const target = e.target.closest?.("[data-card-idx]");
            const idx = target ? Number(target.getAttribute("data-card-idx")) : -1;
            setPaused((p) => !p);
            setHighlightedIdx((prev) => (prev === idx ? -1 : idx));
        } else {
            // Swipe → clear highlight, ensure auto-scroll runs.
            setHighlightedIdx(-1);
            setPaused(false);
        }
        // Reset drag flag after a beat so rAF resumes cleanly.
        setTimeout(() => { isDraggingRef.current = false; }, 50);
    };

    // Desktop click fallback for interactive path — pure click (not drag).
    const onClick = (e) => {
        if (interactive) return; // handled by onTouchEnd on mobile
        togglePause();
    };

    if (interactive) {
        return (
            <div
                ref={scrollRef}
                className="relative overflow-x-auto overflow-y-hidden py-1"
                style={{
                    maskImage:
                        "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
                    WebkitMaskImage:
                        "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    scrollBehavior: "auto",
                    WebkitOverflowScrolling: "touch",
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onClick={(e) => {
                    // Desktop click support: tap the card to toggle pause+highlight
                    const target = e.target.closest?.("[data-card-idx]");
                    const idx = target ? Number(target.getAttribute("data-card-idx")) : -1;
                    setPaused((p) => !p);
                    setHighlightedIdx((prev) => (prev === idx ? -1 : idx));
                }}
                data-testid="reviews-marquee"
            >
                <div className="flex items-stretch" style={{ width: "max-content" }}>
                    {doubled.map((r, i) => (
                        <ReviewCard
                            key={`${r.author}-${i}`}
                            r={r}
                            cardIdx={i}
                            highlighted={highlightedIdx === i}
                        />
                    ))}
                </div>
                {/* Hide native scrollbar in WebKit/Chromium */}
                <style>{`[data-testid="reviews-marquee"]::-webkit-scrollbar{display:none}`}</style>
            </div>
        );
    }

    return (
        <div
            className="relative overflow-hidden py-1"
            style={{
                maskImage:
                    "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
                WebkitMaskImage:
                    "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            }}
            onClick={onClick}
            data-testid="reviews-marquee"
        >
            <div
                className="flex"
                style={{
                    animation: `${reverse ? "reviews-marquee-reverse" : "reviews-marquee"} ${speed}s linear infinite`,
                    animationPlayState: paused ? "paused" : "running",
                    width: "max-content",
                }}
            >
                {doubled.map((r, i) => (
                    <ReviewCard key={`${r.author}-${i}`} r={r} />
                ))}
            </div>
        </div>
    );
}
